import { parse, stringify } from "query-string"

// Shared, single source of truth for serializing the /assets search+filter
// state into the URL query string and back. The header search and the /assets
// listing both go through these two functions so the param names can never
// drift apart. Pagination uses `p`; everything else mirrors the filterBody
// shape the page already POSTs to /api/asset-groups.

// The defaults below match the page's `firstBody`. We omit any value equal to
// its default so a clean search produces a short, shareable URL.
const DEFAULT_TYPE = ["physical"]
const DEFAULT_SORT = [{ created: "desc" }]
const DEFAULT_IN_STOCK_ONLY = true
const DEFAULT_ITEM_PER_PAGE = "15"

// Consistent array encoding for both directions. query-string's "bracket"
// format renders categories=1&categories=2 as categories[]=1&categories[]=2,
// which round-trips arrays cleanly regardless of element count.
const ARRAY_FORMAT = { arrayFormat: "bracket" }

// Compare two arrays by their JSON form. Used to detect whether a multi-select
// (type, sort) still holds its default value and can therefore be omitted.
const sameArray = (a, b) => JSON.stringify(a) === JSON.stringify(b)

/**
 * Serialize a filterBody into a query string (without the leading "?").
 *
 * Only non-default, non-empty fields are emitted, so an untouched search yields
 * an empty string. `sort` is an array of single-key objects; we JSON-encode it
 * into one `sort` param and decode it symmetrically in searchToFilterBody.
 */
export const filterBodyToSearch = filterBody => {
  const body = filterBody || {}
  const params = {}

  // Free-text search lives under `search` to match the /api/asset-groups
  // contract: the backend reads `search`, and a `title` key is silently ignored.
  // Also accept a legacy `title` value (e.g. a saved search persisted before the
  // title→search rename, which reloads its criteria straight into the filterBody)
  // so its text is not silently dropped on reload; the modern `search` key wins
  // when both are present. This mirrors the `title` fallback in searchToFilterBody.
  // Trim so a whitespace-only entry does not pollute the URL with %20 values.
  const freeText =
    typeof body.search === "string" && body.search.trim()
      ? body.search
      : typeof body.title === "string"
        ? body.title
        : ""
  const trimmedSearch = freeText.trim()
  if (trimmedSearch) params.search = trimmedSearch

  if (Array.isArray(body.categories) && body.categories.length)
    params.categories = body.categories
  if (Array.isArray(body.brand) && body.brand.length) params.brand = body.brand
  if (Array.isArray(body.tags) && body.tags.length) params.tags = body.tags

  // `type` defaults to ["physical"]; only serialize when it differs.
  if (Array.isArray(body.type) && !sameArray(body.type, DEFAULT_TYPE))
    params.type = body.type

  // `sort` is structured ([{created:"desc"}]); JSON-encode it deterministically
  // and skip when it still equals the default newest-first ordering.
  if (Array.isArray(body.sort) && !sameArray(body.sort, DEFAULT_SORT))
    params.sort = JSON.stringify(body.sort)

  // `in_stock_only` defaults to true; only serialize the non-default value.
  if (
    typeof body.in_stock_only === "boolean" &&
    body.in_stock_only !== DEFAULT_IN_STOCK_ONLY
  )
    params.in_stock_only = body.in_stock_only

  // Page size: emit only when the user picked a non-default view (e.g. 30/45).
  if (
    body.item_per_page &&
    body.item_per_page.toString() !== DEFAULT_ITEM_PER_PAGE
  )
    params.item_per_page = body.item_per_page.toString()

  // Pagination: emit `p` only when past the first page.
  if (body.p && parseInt(body.p, 10) > 1) params.p = body.p

  return stringify(params, ARRAY_FORMAT)
}

/**
 * Parse a `location.search` string back into a filterBody, merging onto the
 * provided defaults so any missing param falls back to the page's default.
 *
 * Types are coerced to match existing usage: arrays stay arrays, `in_stock_only`
 * becomes a boolean, and the page number stays a string (the page parses it).
 */
export const searchToFilterBody = (search, defaults) => {
  const base = { ...(defaults || {}) }
  const parsed = parse(search || "", ARRAY_FORMAT)

  if (typeof parsed.search === "string") base.search = parsed.search
  else if (typeof parsed.title === "string") base.search = parsed.title

  // query-string returns a single value (string) or an array. Normalize each
  // multi-value field to an array so the filter UI always receives arrays.
  const toArray = v => (Array.isArray(v) ? v : v == null ? [] : [v])
  if ("categories" in parsed) base.categories = toArray(parsed.categories)
  if ("brand" in parsed) base.brand = toArray(parsed.brand)
  if ("tags" in parsed) base.tags = toArray(parsed.tags)
  if ("type" in parsed) base.type = toArray(parsed.type)

  if (typeof parsed.sort === "string") {
    try {
      const decoded = JSON.parse(parsed.sort)
      // Only accept sort objects whose keys and directions are in the allowed
      // set that the UI actually offers, falling back to the default on anything
      // unexpected so an arbitrary URL cannot drive unbounded API sort fields.
      const ALLOWED_SORT_KEYS = ["title", "created", "changed"]
      const ALLOWED_SORT_DIRS = ["asc", "desc"]
      const isValidSortItem = item =>
        item !== null &&
        typeof item === "object" &&
        !Array.isArray(item) &&
        Object.keys(item).length === 1 &&
        ALLOWED_SORT_KEYS.includes(Object.keys(item)[0]) &&
        ALLOWED_SORT_DIRS.includes(Object.values(item)[0])
      if (Array.isArray(decoded) && decoded.every(isValidSortItem))
        base.sort = decoded
    } catch {
      // Ignore a malformed sort param and keep the default ordering.
    }
  }

  if ("in_stock_only" in parsed)
    base.in_stock_only = parsed.in_stock_only === "true"

  // Page size is kept as a string to match the filterBody/asset-groups shape.
  if (typeof parsed.item_per_page === "string")
    base.item_per_page = parsed.item_per_page

  // Page number is kept as a string to match how the page reads `?p=`.
  if (parsed.p) base.p = parsed.p

  return base
}
